import { useMemo, useState } from 'react';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import { GeneralPlanCard } from '~/design-system/components/Pricing/GeneralPlanCard';
import { TopUpCardUI } from '~/design-system/components/Pricing/TopUpCardUI';
import { BillingCtaButton } from '~/design-system/components/v1_5/Settings';
import { FREE_CREDITS, PRO_LADDER } from '~/design-system/components/Pricing/pricingContent';
import type { CreditPack } from '~/queries/subscription';
import styles from './PricingSection.module.css';

// The REAL billing components (GeneralPlanCard + TopUpCardUI), rendered signed-out:
// sliders and the annual toggle stay live, CTAs lead to sign-in. The plan ladder is
// the same PRO_LADDER constant the billing page uses; top-up packs mirror the packs
// configured in Stripe (display only — checkout happens after sign-in).
const LANDING_CREDIT_PACKS: CreditPack[] = [
  { priceId: 'landing-pack-80', credits: 80, amountCents: 500, currency: 'usd' },
  { priceId: 'landing-pack-160', credits: 160, amountCents: 1000, currency: 'usd' },
  { priceId: 'landing-pack-400', credits: 400, amountCents: 2500, currency: 'usd' },
  { priceId: 'landing-pack-800', credits: 800, amountCents: 5000, currency: 'usd' },
];

const SIGN_IN_HREF = '/sign-in';

// The only address published anywhere in the app today (Terms + Privacy). Swap it for a real sales
// inbox when there is one — passed as a prop so it is a one-line change.
const ENTERPRISE_HREF = 'mailto:data@deck.team';

// Same ladder as /billing, with the free 30-credit tier as the first stop.
const LANDING_LADDER = [
  { credits: FREE_CREDITS, monthlyAmount: 0, yearlyAmount: 0 },
  ...PRO_LADDER,
];

export interface PricingSectionProps {
  id?: string;
  className?: string;
  /** `dark` is the landing v2 ground: the pills invert, everything else follows the page's tokens. */
  tone?: 'light' | 'dark';
  headline?: string;
  enterpriseTitle?: string;
  enterpriseBody?: string;
  enterpriseCtaLabel?: string;
  enterpriseHref?: string;
}

export function PricingSection({
  id,
  className,
  headline = 'Build your first Deck free. Need more? Upgrade.',
  enterpriseTitle = 'Team or Enterprise?',
  enterpriseBody = 'Bigger teams, custom credit limits and invoicing. Tell us what you need.',
  enterpriseCtaLabel = 'Contact us',
  enterpriseHref = ENTERPRISE_HREF,
  tone = 'light',
}: PricingSectionProps) {
  const [annual, setAnnual] = useState(false);
  const [planIndex, setPlanIndex] = useState(0);
  const [packIndex, setPackIndex] = useState(0);

  const step = LANDING_LADDER[planIndex];
  const isFree = step.monthlyAmount === 0;
  const headlinePrice = annual ? step.yearlyAmount / 12 / 100 : step.monthlyAmount / 100;

  const explanationLines = useMemo(
    () =>
      isFree
        ? [{ icon: 'loop-right', text: `${FREE_CREDITS} free credits, refreshed every month` }]
        : [
            { icon: 'check-fill', text: 'Everything in free, with more credits' },
            { icon: 'loop-right', text: `Refreshes to ${step.credits.toLocaleString('en-US')} credits every month` },
          ],
    [isFree, step.credits],
  );

  const goSignIn = () => {
    window.location.href = SIGN_IN_HREF;
  };

  return (
    <section
      id={id}
      className={cn(landingType.sectionVerticalPadding, styles.section, tone === 'dark' && styles.sectionDark, className)}
      data-name="PricingSection"
    >
      <div className={cn(landingType.sectionInset, styles.container)}>
        <div className={styles.heading}>
          <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
            {headline}
          </ScrollRevealHeadline>
        </div>

        <div className={styles.cards}>
          <GeneralPlanCard
            annual={annual}
            onAnnualChange={setAnnual}
            headlineCredits={step.credits}
            headlinePrice={headlinePrice}
            sliderValues={LANDING_LADDER.map((s) => s.credits)}
            selectedIndex={planIndex}
            onVolumeChange={setPlanIndex}
            buttonLabel={isFree ? 'Get started free' : 'Get started'}
            // The section's tone, not the reader's appearance: the v2 page pins `dark` regardless of
            // which theme the app is in, and the CTA has to agree with the card it sits on.
            theme={tone}
            explanationLines={explanationLines}
            onUpgrade={goSignIn}
          />
          <div className={styles.sideColumn}>
            <TopUpCardUI
              packs={LANDING_CREDIT_PACKS}
              selectedIndex={packIndex}
              onSelectedIndexChange={setPackIndex}
              onBuy={goSignIn}
              theme={tone}
            />

            {/*
              * A div with a real button, which is what `BillingTeamsCard` — this card's signed-in twin
              * — already is: text block, then a `soft` pill. The card used to be one big `<a>` with a
              * span painted to look like a button, and a `<button>` cannot live inside an anchor, so
              * adopting the real control meant giving up the card-wide link. That is the right trade
              * here: the href is a `mailto:`, where a whole-card target buys nothing a button does not
              * (no new tab, no middle-click, nothing to preview in the status bar).
              */}
            <div className={styles.enterpriseCard}>
              <div className={styles.enterpriseTitleBlock}>
                <div className={styles.enterpriseTitle}>{enterpriseTitle}</div>
                <p className={styles.enterpriseBody}>{enterpriseBody}</p>
              </div>
              <BillingCtaButton
                skin="soft"
                className={styles.enterpriseCta}
                theme={tone}
                onClick={() => {
                  window.location.href = enterpriseHref;
                }}
              >
                {enterpriseCtaLabel}
              </BillingCtaButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

PricingSection.displayName = 'PricingSection';
